import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root'
})
export class GraphQLQueriesService {

    private collectionsMappers: Record<string, any> = {
        cryptophunks: {
            GET_OWNED: (data: any) => data,
            GET_ALL_LISTINGS_BIDS: (data: any) => data,
            GET_PUNK: (data: any) => data,
            GET_EVENTS: (data: any) => data,
        },
        'cryptopunks-v1': {
            GET_USER_SENT_SALES: (data: any) => {
                // Map alternative API events to the legacy Event shape for sent sales
                console.log('[GraphQLQueriesService] Transforming GET_USER_SENT_SALES for cryptopunks-v1');

                if (!data || !data.getAllEvents) {
                    return [];
                }

                const ethToWei = (eth: string | number): string => {
                    if (!eth || eth === '0') return '0';
                    const ethValue = typeof eth === 'string' ? parseFloat(eth) : eth;
                    const weiValue = ethValue * Math.pow(10, 18);
                    return Math.floor(weiValue).toString();
                };

                const isoToTimestamp = (isoDate: string): string => {
                    if (!isoDate) return '0';
                    const date = new Date(isoDate);
                    return Math.floor(date.getTime() / 1000).toString();
                };

                const formatUsdValue = (usdValue: number | string, ethValue: number | string): string => {
                    if (!usdValue || usdValue === '0' || !ethValue || ethValue === '0') return '0';
                    const usd = typeof usdValue === 'string' ? parseFloat(usdValue) : usdValue;
                    const eth = typeof ethValue === 'string' ? parseFloat(ethValue) : ethValue;
                    const rate = (usd / eth) * Math.pow(10, 8);
                    return Math.floor(rate).toString();
                };

                const mapPlatform = (platform: string): string => {
                    if (!platform) return 'unknown';
                    return platform === 'blurio' ? 'blur' : platform;
                };

                const wrappedPunksAddr = environment.wrappedPunksAddress?.toLowerCase() || '';

                return (data.getAllEvents || [])
                    .filter((event: any) => (event?.eventType || '').toLowerCase() === 'sale' && event?.amount && parseFloat(event.amount) > 0)
                    .map((event: any) => ({
                        type: 'Sale',
                        platform: mapPlatform(event.platform) || 'unknown',
                        value: ethToWei(event.amount),
                        transactionHash: event.tx,
                        collection: event.collection?.toLowerCase(),
                        wrapped: event.wrapped === true || (event.collection?.toLowerCase() || '') === wrappedPunksAddr,
                        usd: formatUsdValue(event.usdValue, event.amount),
                        blockTimestamp: isoToTimestamp(event.txDate),
                        tokenId: `${event.tokenId}`,
                        fromAccount: { id: event.fromWallet?.toLowerCase() || '0x0000000000000000000000000000000000000000' },
                        toAccount: { id: event.toWallet?.toLowerCase() || '0x0000000000000000000000000000000000000000' },
                    }));
            },
            GET_USER_RECEIVED_SALES: (data: any) => {
                // Map alternative API events to the legacy Event shape for received sales
                console.log('[GraphQLQueriesService] Transforming GET_USER_RECEIVED_SALES for cryptopunks-v1');

                if (!data || !data.getAllEvents) {
                    return [];
                }

                const ethToWei = (eth: string | number): string => {
                    if (!eth || eth === '0') return '0';
                    const ethValue = typeof eth === 'string' ? parseFloat(eth) : eth;
                    const weiValue = ethValue * Math.pow(10, 18);
                    return Math.floor(weiValue).toString();
                };

                const isoToTimestamp = (isoDate: string): string => {
                    if (!isoDate) return '0';
                    const date = new Date(isoDate);
                    return Math.floor(date.getTime() / 1000).toString();
                };

                const formatUsdValue = (usdValue: number | string, ethValue: number | string): string => {
                    if (!usdValue || usdValue === '0' || !ethValue || ethValue === '0') return '0';
                    const usd = typeof usdValue === 'string' ? parseFloat(usdValue) : usdValue;
                    const eth = typeof ethValue === 'string' ? parseFloat(ethValue) : ethValue;
                    const rate = (usd / eth) * Math.pow(10, 8);
                    return Math.floor(rate).toString();
                };

                const mapPlatform = (platform: string): string => {
                    if (!platform) return 'unknown';
                    return platform === 'blurio' ? 'blur' : platform;
                };

                const wrappedPunksAddr = environment.wrappedPunksAddress?.toLowerCase() || '';

                return (data.getAllEvents || [])
                    .filter((event: any) => (event?.eventType || '').toLowerCase() === 'sale' && event?.amount && parseFloat(event.amount) > 0)
                    .map((event: any) => ({
                        type: 'Sale',
                        platform: mapPlatform(event.platform) || 'unknown',
                        value: ethToWei(event.amount),
                        transactionHash: event.tx,
                        collection: event.collection?.toLowerCase(),
                        wrapped: event.wrapped === true || (event.collection?.toLowerCase() || '') === wrappedPunksAddr,
                        usd: formatUsdValue(event.usdValue, event.amount),
                        blockTimestamp: isoToTimestamp(event.txDate),
                        tokenId: `${event.tokenId}`,
                        fromAccount: { id: event.fromWallet?.toLowerCase() || '0x0000000000000000000000000000000000000000' },
                        toAccount: { id: event.toWallet?.toLowerCase() || '0x0000000000000000000000000000000000000000' },
                    }));
            }
        },
        cryptopunks: {
            GET_OWNED: (data: any) => data,
            GET_ALL_LISTINGS_BIDS: (data: any) => data,
            GET_PUNK: (data: any) => data,
            GET_EVENTS: (data: any) => data,
            GET_TX_HISTORY: (data: any) => {
                // Transform new API format to match expected format
                console.log('[GraphQLQueriesService] Transforming GET_TX_HISTORY for cryptopunks');
                
                if (!data || !data.getAllEvents) {
                    return [];
                }
                
                return data.getAllEvents.map((event: any) => {

                    // Convert ETH to Wei (1 ETH = 10^18 Wei)
                    const ethToWei = (eth: string | number): string => {
                        if (!eth || eth === '0') return '0';
                        const ethValue = typeof eth === 'string' ? parseFloat(eth) : eth;
                        const weiValue = ethValue * Math.pow(10, 18);
                        return Math.floor(weiValue).toString();
                    };

                    // Convert ISO date to Unix timestamp
                    const isoToTimestamp = (isoDate: string): string => {
                        if (!isoDate) return '0';
                        const date = new Date(isoDate);
                        return Math.floor(date.getTime() / 1000).toString();
                    };

                    // Convert USD value to the expected format for the usd pipe
                    // The usd pipe expects a rate (USD per ETH) with 8 decimals
                    // So we need to calculate: usdValue / ethValue * 10^8
                    const formatUsdValue = (usdValue: number | string, ethValue: number | string): string => {
                        if (!usdValue || usdValue === '0' || !ethValue || ethValue === '0') return '0';
                        const usd = typeof usdValue === 'string' ? parseFloat(usdValue) : usdValue;
                        const eth = typeof ethValue === 'string' ? parseFloat(ethValue) : ethValue;
                        const rate = (usd / eth) * Math.pow(10, 8);
                        return Math.floor(rate).toString();
                    };

                    // Map event types to expected format
                    const mapEventType = (eventType: string, fromWallet: string, toWallet: string): string => {
                        // Check for wrapping events based on addresses
                        const zeroAddress = '0x0000000000000000000000000000000000000000';
                        const wrappingAddresses = [
                            '0xd3fb1e749237739956fa1b244555b81e81b16cb8',
                            '0xb7f7f6c52f2e2fdb1963eab30438024864c313f6'
                        ];
                        // If the source is zeroAddress and the destination is a wrapping address, it's a "Wrapped" event
                        if (fromWallet === zeroAddress && eventType === 'mint') {
                            return environment.collection !== 'cryptophunks' ? 'Wrapped' : 'Minted';
                        }
                        // If the destination is zeroAddress, it's an "Unwrapped" event
                        if (toWallet === zeroAddress) {
                            return 'Unwrapped';
                        }

                        if (fromWallet === zeroAddress && eventType === 'sale') {
                            return 'Wrapped';
                        }
                        
                        const typeMapping: Record<string, string> = {
                            'bid': 'BidEntered',
                            'offer-canceled': 'OfferWithdrawn',
                            'bid-withdrawn': 'BidWithdrawn',
                            'transfer': 'Transferred',
                            'assign': environment.collection === 'cryptopunks-v1' ? 'V1Airdrop' : 'V2Airdrop',
                        };

                        if (eventType === 'sale' && !event.amount) {
                            return 'Transferred';
                        }

                        if (eventType === 'sale') return 'Sale';

                        return typeMapping[eventType] || eventType;
                    };

                    // Map platform field: if platform is 'blurio', map to 'blur'
                    const mapPlatform = (platform: string): string => {
                        if (!platform) return 'unknown';
                        return platform === 'blurio' ? 'blur' : platform;
                    };

                    return {
                        // Map new fields to expected format
                        type: mapEventType(event.eventType, event.fromWallet, event.toWallet),
                        platform: mapPlatform(event.platform) || 'unknown',
                        value: ethToWei(event.amount), // Convert ETH to Wei
                        transactionHash: event.tx, // tx maps to transactionHash
                        usd: formatUsdValue(event.usdValue, event.amount), // Convert USD to rate format
                        blockTimestamp: isoToTimestamp(event.txDate), // Convert ISO date to Unix timestamp
                        fromAccount: {
                            id: event.fromWallet?.toLowerCase() || '0x0000000000000000000000000000000000000000'
                        },
                        toAccount: {
                            id: event.toWallet?.toLowerCase() || '0x0000000000000000000000000000000000000000'
                        }
                    };
                });
            },
            GET_SALES_HISTORY: (data: any) => {
                // Map alternative API events to the legacy Event shape used by sales list
                console.log('[GraphQLQueriesService] Transforming GET_SALES_HISTORY for cryptopunks');

                if (!data || !data.getAllEvents) {
                    return [];
                }

                const ethToWei = (eth: string | number): string => {
                    if (!eth || eth === '0') return '0';
                    const ethValue = typeof eth === 'string' ? parseFloat(eth) : eth;
                    const weiValue = ethValue * Math.pow(10, 18);
                    return Math.floor(weiValue).toString();
                };

                const isoToTimestamp = (isoDate: string): string => {
                    if (!isoDate) return '0';
                    const date = new Date(isoDate);
                    return Math.floor(date.getTime() / 1000).toString();
                };

                // Convert USD value to the expected format for the usd pipe (rate with 8 decimals)
                const formatUsdValue = (usdValue: number | string, ethValue: number | string): string => {
                    if (!usdValue || usdValue === '0' || !ethValue || ethValue === '0') return '0';
                    const usd = typeof usdValue === 'string' ? parseFloat(usdValue) : usdValue;
                    const eth = typeof ethValue === 'string' ? parseFloat(ethValue) : ethValue;
                    const rate = (usd / eth) * Math.pow(10, 8);
                    return Math.floor(rate).toString();
                };

                const mapPlatform = (platform: string): string => {
                    if (!platform) return 'unknown';
                    return platform === 'blurio' ? 'blur' : platform;
                };

                const wrappedPunksAddr = environment.wrappedPunksAddress?.toLowerCase() || '';
                const isWrappedSale = (event: any): boolean => {
                    if (event?.wrapped === true) {
                        return true;
                    }
                    return (event?.collection?.toLowerCase() || '') === wrappedPunksAddr;
                };

                return (data.getAllEvents || [])
                    .filter((event: any) => (event?.eventType || '').toLowerCase() === 'sale')
                    .map((event: any) => ({
                        type: 'Sale',
                        platform: mapPlatform(event.platform) || 'unknown',
                        value: ethToWei(event.amount),
                        transactionHash: event.tx,
                        usd: formatUsdValue(event.usdValue, event.amount),
                        blockTimestamp: isoToTimestamp(event.txDate),
                        tokenId: `${event.tokenId}`,
                        toAccount: { id: event.toWallet || '0x0000000000000000000000000000000000000000' },
                        fromAccount: { id: event.fromWallet || '0x0000000000000000000000000000000000000000' },
                        wrapped: isWrappedSale(event),
                    }));
            },
            GET_USER_SENT_SALES: (data: any) => {
                // Map alternative API events to the legacy Event shape for sent sales
                console.log('[GraphQLQueriesService] Transforming GET_USER_SENT_SALES for cryptopunks');

                if (!data || !data.getAllEvents) {
                    return [];
                }

                const ethToWei = (eth: string | number): string => {
                    if (!eth || eth === '0') return '0';
                    const ethValue = typeof eth === 'string' ? parseFloat(eth) : eth;
                    const weiValue = ethValue * Math.pow(10, 18);
                    return Math.floor(weiValue).toString();
                };

                const isoToTimestamp = (isoDate: string): string => {
                    if (!isoDate) return '0';
                    const date = new Date(isoDate);
                    return Math.floor(date.getTime() / 1000).toString();
                };

                const formatUsdValue = (usdValue: number | string, ethValue: number | string): string => {
                    if (!usdValue || usdValue === '0' || !ethValue || ethValue === '0') return '0';
                    const usd = typeof usdValue === 'string' ? parseFloat(usdValue) : usdValue;
                    const eth = typeof ethValue === 'string' ? parseFloat(ethValue) : ethValue;
                    const rate = (usd / eth) * Math.pow(10, 8);
                    return Math.floor(rate).toString();
                };

                const mapPlatform = (platform: string): string => {
                    if (!platform) return 'unknown';
                    return platform === 'blurio' ? 'blur' : platform;
                };

                return (data.getAllEvents || [])
                    .filter((event: any) => (event?.eventType || '').toLowerCase() === 'sale' && event?.amount && parseFloat(event.amount) > 0)
                    .map((event: any) => ({
                        type: 'Sale',
                        platform: mapPlatform(event.platform) || 'unknown',
                        value: ethToWei(event.amount),
                        transactionHash: event.tx,
                        usd: formatUsdValue(event.usdValue, event.amount),
                        blockTimestamp: isoToTimestamp(event.txDate),
                        tokenId: `${event.tokenId}`,
                        fromAccount: { id: event.fromWallet?.toLowerCase() || '0x0000000000000000000000000000000000000000' },
                        toAccount: { id: event.toWallet?.toLowerCase() || '0x0000000000000000000000000000000000000000' },
                    }));
            },
            GET_USER_RECEIVED_SALES: (data: any) => {
                // Map alternative API events to the legacy Event shape for received sales
                console.log('[GraphQLQueriesService] Transforming GET_USER_RECEIVED_SALES for cryptopunks');

                if (!data || !data.getAllEvents) {
                    return [];
                }

                const ethToWei = (eth: string | number): string => {
                    if (!eth || eth === '0') return '0';
                    const ethValue = typeof eth === 'string' ? parseFloat(eth) : eth;
                    const weiValue = ethValue * Math.pow(10, 18);
                    return Math.floor(weiValue).toString();
                };

                const isoToTimestamp = (isoDate: string): string => {
                    if (!isoDate) return '0';
                    const date = new Date(isoDate);
                    return Math.floor(date.getTime() / 1000).toString();
                };

                const formatUsdValue = (usdValue: number | string, ethValue: number | string): string => {
                    if (!usdValue || usdValue === '0' || !ethValue || ethValue === '0') return '0';
                    const usd = typeof usdValue === 'string' ? parseFloat(usdValue) : usdValue;
                    const eth = typeof ethValue === 'string' ? parseFloat(ethValue) : ethValue;
                    const rate = (usd / eth) * Math.pow(10, 8);
                    return Math.floor(rate).toString();
                };

                const mapPlatform = (platform: string): string => {
                    if (!platform) return 'unknown';
                    return platform === 'blurio' ? 'blur' : platform;
                };

                return (data.getAllEvents || [])
                    .filter((event: any) => (event?.eventType || '').toLowerCase() === 'sale' && event?.amount && parseFloat(event.amount) > 0)
                    .map((event: any) => ({
                        type: 'Sale',
                        platform: mapPlatform(event.platform) || 'unknown',
                        value: ethToWei(event.amount),
                        transactionHash: event.tx,
                        usd: formatUsdValue(event.usdValue, event.amount),
                        blockTimestamp: isoToTimestamp(event.txDate),
                        tokenId: `${event.tokenId}`,
                        fromAccount: { id: event.fromWallet?.toLowerCase() || '0x0000000000000000000000000000000000000000' },
                        toAccount: { id: event.toWallet?.toLowerCase() || '0x0000000000000000000000000000000000000000' },
                    }));
            }
        }
    };

    private collectionsQueries: Record<string, any> = {
        'cryptopunks-v1': {
            GET_PUNK: gql`
                query GetPunk($id: String!) {
                    punk(id: $id) {
                        id
                        listing {
                            value
                            toAccount {
                                id
                            }
                        }
                        owner {
                            id
                        }
                        wrapped
                        bid {
                            fromAccount {
                                id
                            }
                            value
                        }
                    }
                }
            `,
            GET_OWNED: gql`
                query GetOwned($address: String!, $limit: Int!, $skip: Int!) {
                    account(id: $address) {
                        id
                        punks(
                            first: $limit,
                            skip: $skip
                        ) {
                            id
                            wrapped
                            listing {
                                id
                                value
                                blockTimestamp
                            }
                            bid {
                                id
                                value
                                blockTimestamp
                            }
                        }
                    }
                }
            `,
            GET_USER_SENT_SALES: gql`
                query GetAllEvents(
                  $collections: [String!],
                  $txFrom: String,
                  $eventType: String
                ) {
                  getAllEvents(
                    collections: $collections,
                    txFrom: $txFrom,
                    eventType: $eventType
                  ) {
                    id
                    platform
                    amount
                    collection
                    eventType
                    toWallet
                    tokenId
                    tx
                    txDate
                    usdValue
                    logIndex
                    fromWallet
                  }
                }
            `,
            GET_USER_RECEIVED_SALES: gql`
                query GetAllEvents(
                  $collections: [String!],
                  $txTo: String,
                  $eventType: String
                ) {
                  getAllEvents(
                    collections: $collections,
                    txTo: $txTo,
                    eventType: $eventType
                  ) {
                    id
                    platform
                    amount
                    collection
                    eventType
                    toWallet
                    tokenId
                    tx
                    txDate
                    usdValue
                    logIndex
                    fromWallet
                  }
                }
            `,
        },
        'cryptophunks': {
            GET_PUNK: gql`
                query GetPunk($id: String!) {
                    punk(id: $id) {
                        id
                        listing {
                            value
                            toAccount {
                                id
                            }
                        }
                        owner {
                            id
                        }
                        wrapped
                        bid {
                            fromAccount {
                                id
                            }
                            value
                        }
                    }
                }
            `,
        },
        cryptopunks: {
            GET_OWNED: gql`
                query GetOwned($address: String!, $limit: Int!, $skip: Int!) {
                    account(id: $address) {
                        id
                        punks(
                            first: $limit,
                            skip: $skip
                        ) {
                            id
                            wrapped
                            c721wrapped
                            listing {
                                id
                                value
                                blockTimestamp
                            }
                            bid {
                                id
                                value
                                blockTimestamp
                            }
                        }
                    }
                }
            `,
            // TODO readd platform here when new graph released
            GET_EVENTS: gql`

            query GetEvents($limit: Int!, $skip: Int!, $toAccountNot: String!, $minValue: Int!, $order: String!, $orderDirection: String!) {
                events(
                  orderBy: $order,
                  orderDirection: $orderDirection,
                  first: $limit,
                  skip: $skip,
                  where: { toAccount_not: $toAccountNot, value_gt: $minValue }
                ) {
                  id
                  type
                  tokenId
                  blockTimestamp
                  value
                  usd
                  toAccount {
                    id
                  }
                }
              }          
            `,

            GET_TX_HISTORY: gql`
                query GetAllEvents($collections: [String!], $tokenId: String, $eventType: String) {
                    getAllEvents(collections: $collections, tokenId: $tokenId, eventType: $eventType) {
                        id
                        platform
                        amount
                        collection
                        eventType
                        toWallet
                        tokenId
                        tx
                        txDate
                        usdValue
                        logIndex
                        fromWallet
                    }
                }
            `,
            GET_SALES_HISTORY: gql`
                query GetAllEvents(
                  $collections: [String!],
                  $orderBy: String,
                  $platform: String,
                  $skip: Int,
                  $tokenId: String,
                  $txFromOrTxToEq: String,
                  $eventType: String
                ) {
                  getAllEvents(
                    collections: $collections,
                    orderBy: $orderBy,
                    platform: $platform,
                    skip: $skip,
                    tokenId: $tokenId,
                    txFromOrTxToEq: $txFromOrTxToEq,
                    eventType: $eventType
                  ) {
                    id
                    platform
                    amount
                    collection
                    eventType
                    toWallet
                    tokenId
                    tx
                    txDate
                    usdValue
                    logIndex
                    fromWallet
                    wrapped
                  }
                }
            `,
            GET_USER_SENT_SALES: gql`
                query GetAllEvents(
                  $collections: [String!],
                  $txFrom: String,
                  $eventType: String
                ) {
                  getAllEvents(
                    collections: $collections,
                    txFrom: $txFrom,
                    eventType: $eventType
                  ) {
                    id
                    platform
                    amount
                    collection
                    eventType
                    toWallet
                    tokenId
                    tx
                    txDate
                    usdValue
                    logIndex
                    fromWallet
                    wrapped
                  }
                }
            `,
            GET_USER_RECEIVED_SALES: gql`
                query GetAllEvents(
                  $collections: [String!],
                  $txTo: String,
                  $eventType: String
                ) {
                  getAllEvents(
                    collections: $collections,
                    txTo: $txTo,
                    eventType: $eventType
                  ) {
                    id
                    platform
                    amount
                    collection
                    eventType
                    toWallet
                    tokenId
                    tx
                    txDate
                    usdValue
                    logIndex
                    fromWallet
                  }
                }
            `,

            /*
            GET_EVENTS: gql`
                query GetEvents($limit: Int!) {
                    events(
                        orderBy: "blockTimestamp",
                        orderDirection: desc,
                        first: $limit
                    ) {
                        type
                        tokenId
                        blockTimestamp
                        value
                        usd
                        toAccount {
                            id
                        }
                    }
                }
            `,

            */
            GET_TOP_SALES: gql`
                query GetTopSales($limit: Int!, $period: Int!) {
                    events(
                        where: {
                            type: "Sale",
                            blockTimestamp_gt: $period
                        },
                        first: $limit,
                        orderBy: value,
                        orderDirection: desc
                    ) {
                        type
                        tokenId
                        value
                        usd
                        blockTimestamp
                        transactionHash
                    }
                }
            `,
            GET_PUNK: gql`
                query GetPunk($id: String!) {
                    punk(id: $id) {
                        id
                        listing {
                            value
                            toAccount {
                                id
                            }
                        }
                        owner {
                            id
                        }
                        wrapped
                        c721wrapped
                        bid {
                            id
                            transactionHash
                            fromAccount {
                                id
                            }
                            value
                        }
                    }
                }
            `,
            GET_ALL_LISTINGS_BIDS: gql`
                query GetListingsBids(
                    $listingSkip: Int!,
                    $bidSkip: Int!,
                    $limit: Int!,
                    $filteredTokenIds: [String!]!
                ) {
                    listings(
                        first: $limit,
                        skip: $listingSkip,
                        orderBy: "blockTimestamp",
                        orderDirection: desc,
                        where: {toAccount: "0x0000000000000000000000000000000000000000"}
                    ) {
                        id
                        value
                        blockTimestamp
                        punk {
                            id
                            owner {
                                id
                            }
                        }
                        fromAccount {
                            id
                        }
                    }

                    bids(
                        first: $limit,
                        skip: $bidSkip,
                        orderBy: "blockTimestamp",
                        orderDirection: desc
                    ) {
                        id
                        value
                        blockTimestamp
                        punk {
                            id
                        }
                    }
                }                
            `,
            GET_LAST_INDEXED_BLOCK: gql`
                query GetLastBlock {
                    _meta {
                        block {
                            number
                        }
                    }
                }
            `
        },
    };

    constructor() {}

    getQuery(collection: string, queryName: string) {
        return this.collectionsQueries[collection]?.[queryName] || this.collectionsQueries['cryptopunks']?.[queryName] || null;
    }
    getMapper(collection: string, queryName: string) {
        return this.collectionsMappers[collection]?.[queryName] || this.collectionsMappers['cryptopunks']?.[queryName] || null;
    }
}